import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiClient } from '../../services/api';
import { Invoice } from '../../types/domain';
import { BillingSubTabKey, formatBillingDate } from './types';
import { InvoiceHistoryTable } from './components/InvoiceHistoryTable';
import { ReceiptViewerModal } from './components/ReceiptViewerModal';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import {
  Building2,
  Plus,
  Crown,
  Sparkles,
  ArrowRight,
} from 'lucide-react';

interface AccountBillingTabProps {
  billingSubTab: BillingSubTabKey;
  onCreateOrgClick: () => void;
}

export const AccountBillingTab: React.FC<AccountBillingTabProps> = ({
  billingSubTab,
  onCreateOrgClick,
}) => {
  const { user, organizations } = useAuth();

  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false);
  const [selectedOrgFilter, setSelectedOrgFilter] = useState<string>('all');
  const [selectedReceipt, setSelectedReceipt] = useState<Invoice | null>(null);
  const [invoicePage, setInvoicePage] = useState<number>(1);
  const [invoiceLimit, setInvoiceLimit] = useState<number>(10);
  const [totalInvoices, setTotalInvoices] = useState<number>(0);

  useEffect(() => {
    if (billingSubTab === 'billing') {
      setIsLoadingInvoices(true);
      const params = new URLSearchParams();
      params.set('page', String(invoicePage));
      params.set('limit', String(invoiceLimit));
      if (selectedOrgFilter !== 'all') {
        params.set('organization_id', selectedOrgFilter);
      }
      apiClient<any>(`/invoices?${params.toString()}`)
        .then((res) => {
          const list = Array.isArray(res) ? res : (Array.isArray(res?.data) ? res.data : []);
          setInvoices(list);
          const total = typeof res?.total === 'number' ? res.total : list.length;
          setTotalInvoices(total);
        })
        .catch((err) => {
          console.warn('Failed to load invoices:', err);
        })
        .finally(() => {
          setIsLoadingInvoices(false);
        });
    }
  }, [billingSubTab, invoicePage, invoiceLimit, selectedOrgFilter]);

  return (
    <div className="space-y-6">
      {/* Subscriptions Tab */}
      {billingSubTab === 'subscriptions' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xs font-semibold text-cf-textPrimary uppercase tracking-wider font-mono">
                Organization Subscriptions
              </h3>
              <p className="text-xs text-cf-textSecondary mt-0.5">
                Plan tiers and ceilings are scoped to each organization. Click Manage Plan to change tiers or review quotas.
              </p>
            </div>
          </div>

          {organizations.length === 0 ? (
            <Card padding="lg" className="text-center space-y-2">
              <Building2 size={28} className="mx-auto text-cf-textMuted" />
              <h4 className="text-xs font-semibold text-cf-textPrimary">No Organizations Found</h4>
              <p className="text-xs text-cf-textSecondary max-w-sm mx-auto">
                You are not currently a member of any organization. Create an organization to manage subscriptions and quotas.
              </p>
              <div className="pt-2">
                <Button
                  type="button"
                  variant="primary"
                  size="xs"
                  onClick={onCreateOrgClick}
                  icon={<Plus size={14} />}
                >
                  Create Organization
                </Button>
              </div>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {organizations.map((org) => {
                const planTierUpper = (org.plan_tier || 'free').toUpperCase();
                const isPaid = planTierUpper !== 'FREE';
                const isPro = planTierUpper === 'PRO';
                const isMax = planTierUpper === 'MAX';
                const isUltraOrEnt = planTierUpper === 'ULTRA' || planTierUpper === 'ENTERPRISE';

                return (
                  <div
                    key={org.id}
                    className="p-4 rounded-lg bg-cf-surface border border-cf-border flex flex-col justify-between space-y-4 hover:border-cf-borderActive transition-colors"
                  >
                    <div>
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-8 h-8 rounded bg-cf-bg border border-cf-border flex items-center justify-center text-cf-textSecondary flex-shrink-0">
                            <Building2 size={16} />
                          </div>
                          <div className="min-w-0">
                            <h4 className="text-xs font-semibold text-cf-textPrimary truncate">
                              {org.name}
                            </h4>
                            <span className="text-xs text-cf-textMuted font-mono truncate block">
                              {org.slug}
                            </span>
                          </div>
                        </div>

                        <span
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold font-mono uppercase tracking-wider border ${
                            isUltraOrEnt
                              ? 'bg-purple-500/10 text-purple-400 border-purple-500/30'
                              : isMax
                              ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                              : isPro
                              ? 'bg-sky-500/10 text-sky-400 border-sky-500/30'
                              : 'bg-cf-bg text-cf-textMuted border-cf-border'
                          }`}
                        >
                          {isUltraOrEnt ? (
                            <Sparkles size={11} />
                          ) : isPaid ? (
                            <Crown size={11} />
                          ) : null}
                          <span>{planTierUpper}</span>
                        </span>
                      </div>

                      <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-cf-border/60 text-center">
                        <div className="p-1.5 rounded bg-cf-bg border border-cf-border/40">
                          <span className="text-xs text-cf-textMuted uppercase block">Your Role</span>
                          <span className="text-xs font-semibold text-cf-textPrimary uppercase">
                            {org.role || 'Member'}
                          </span>
                        </div>
                        <div className="p-1.5 rounded bg-cf-bg border border-cf-border/40">
                          <span className="text-xs text-cf-textMuted uppercase block">Projects</span>
                          <span className="text-xs font-semibold text-cf-textPrimary">
                            {org.project_count || 1}
                          </span>
                        </div>
                        <div className="p-1.5 rounded bg-cf-bg border border-cf-border/40">
                          <span className="text-xs text-cf-textMuted uppercase block">Seats</span>
                          <span className="text-xs font-semibold text-cf-textPrimary">
                            {org.member_count || 1}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-cf-border flex items-center justify-between gap-2">
                      <div className="text-xs text-cf-textMuted">
                        Renewal: <span className="text-cf-textPrimary font-medium">{formatBillingDate(org.current_period_end)}</span>
                      </div>
                      <Link
                        to={`/organization/${org.id}/settings?tab=plan`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-cf-bg hover:bg-cf-surfaceHover border border-cf-border text-cf-textPrimary text-xs font-medium transition-colors cursor-pointer"
                      >
                        <span>Manage Plan & Quotas</span>
                        <ArrowRight size={13} />
                      </Link>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Billing / Invoices Tab */}
      {billingSubTab === 'billing' && (
        <InvoiceHistoryTable
          invoices={invoices}
          isLoading={isLoadingInvoices}
          totalInvoices={totalInvoices}
          page={invoicePage}
          pageSize={invoiceLimit}
          onPageChange={setInvoicePage}
          onPageSizeChange={(s) => {
            setInvoiceLimit(s);
            setInvoicePage(1);
          }}
          selectedOrgFilter={selectedOrgFilter}
          onOrgFilterChange={(orgId) => {
            setSelectedOrgFilter(orgId);
            setInvoicePage(1);
          }}
          organizations={organizations}
          onSelectReceipt={setSelectedReceipt}
        />
      )}

      {/* Receipt Viewer Modal */}
      <ReceiptViewerModal
        selectedReceipt={selectedReceipt}
        onClose={() => setSelectedReceipt(null)}
        userEmail={user?.email}
        userFullName={user?.full_name}
      />
    </div>
  );
};
